let previousCameraPosition;

// amazingly simple, via https://codepen.io/ma77os/pen/OJPVrP
function lerp(start, end, amt) {
	return (1 - amt) * start + amt * end;
}

const isValid = (val: any): val is number => typeof val === "number" && !isNaN(val) && isFinite(val);

/**
 * Where the camera has to stand on the ground so that `targetPosition` sits at the leading edge of
 * the frame, given its pitch, bearing and altitude. Pure: no smoothing, no memory of the last call.
 * `computeCameraPosition` below is this plus the optional smoothing the sequences use; the tile
 * warm-up calls this one directly so that walking the route ahead of the render leaves the
 * sequences' smoothing state exactly as it found it.
 */
export const cameraGroundPosition = (pitch: number, bearing: number, targetPosition: { lng: number; lat: number }, altitude: number) => {
	const lng = targetPosition?.lng;
	const lat = targetPosition?.lat;
	if (!isValid(lng) || !isValid(lat) || !isValid(altitude) || altitude <= 0) {
		return { lng: lng || 0, lat: lat || 0 };
	}
	const bearingInRadian = bearing / 57.29;
	const pitchInRadian = (90 - pitch) / 57.29;
	const c = 24902.0; // earth circumference in miles
	const d = ((Math.cos((lat * Math.PI) / 180.0) * c) / 360.0) * 1.609344;
	const lngDiff = ((altitude / Math.tan(pitchInRadian)) * Math.sin(-bearingInRadian)) / (d * 1000);
	const latDiff = ((altitude / Math.tan(pitchInRadian)) * Math.cos(-bearingInRadian)) / 111111; // 110km/degree latitude
	const correctedLng = lng + lngDiff;
	const correctedLat = lat - latDiff;
	if (!isValid(correctedLng) || !isValid(correctedLat)) return null;
	return {
		lng: ((((correctedLng + 180) % 360) + 360) % 360) - 180,
		lat: Math.max(-85, Math.min(85, correctedLat)),
	};
};

export const computeCameraPosition = (pitch, bearing, targetPosition, altitude, smooth = false) => {
	const lng = targetPosition?.lng;
	const lat = targetPosition?.lat;

	// Return target position as-is if inputs are invalid (safe fallback: camera stays at target)
	if (!isValid(lng) || !isValid(lat) || !isValid(altitude) || altitude <= 0) {
		return { lng: lng || 0, lat: lat || 0 };
	}

	var bearingInRadian = bearing / 57.29;
	var pitchInRadian = (90 - pitch) / 57.29;

	const c = 24902.0; // earth circumference in miles
	const d = ((Math.cos((targetPosition?.lat * Math.PI) / 180.0) * c) / 360.0) * 1.609344;

	var lngDiff = ((altitude / Math.tan(pitchInRadian)) * Math.sin(-bearingInRadian)) / (d * 1000);
	var latDiff = ((altitude / Math.tan(pitchInRadian)) * Math.cos(-bearingInRadian)) / 111111; // 110km/degree latitude

	var correctedLng = lng + lngDiff;
	var correctedLat = lat - latDiff;

	// Guard against NaN results from edge-case math (e.g., tan(0) division)
	if (!isValid(correctedLng) || !isValid(correctedLat)) {
		return previousCameraPosition || { lng, lat };
	}

	// Pulling the camera back is a fixed offset in degrees, and on a long leg that offset can exceed
	// the planet: at 8,500,000 m and a 60 degree pitch it works out to 132 degrees of latitude, which
	// sends the camera past the pole and makes Mapbox throw "Invalid LngLat latitude value". Clamp to
	// the Mercator limits -- at that altitude the whole globe is in shot anyway, so the clamped
	// position looks the same and the render survives.
	const newCameraPosition = {
		lng: ((((correctedLng + 180) % 360) + 360) % 360) - 180,
		lat: Math.max(-85, Math.min(85, correctedLat)),
	};

	if (smooth) {
		if (previousCameraPosition) {
			const SMOOTH_FACTOR = 0.6;
			newCameraPosition.lng = lerp(newCameraPosition.lng, previousCameraPosition.lng, SMOOTH_FACTOR);
			newCameraPosition.lat = lerp(newCameraPosition.lat, previousCameraPosition.lat, SMOOTH_FACTOR);
		}
	}

	previousCameraPosition = newCameraPosition;

	return newCameraPosition;
};
